<template>
  <div>
    <div v-if="isReiseOhneFlugOptionalOhneFluege" class="text-center p-3">
      Reise ohne Flug - optional buchbar
    </div>
    <template v-else>
      <div class="d-flex justify-content-between align-items-center">
        <b-button-group size="sm" style="width: 226px">
          <b-button
            v-for="tab in tabs"
            :variant="activeTab === tab.value ? 'primary' : ''"
            @click="activeTab = tab.value"
            style="width: 100%; height: 30px"
            :key="tab.value"
            class="mr-1"
          >
            <div v-if="tab.show">
              {{ tab.text }}
            </div>
          </b-button>
        </b-button-group>

        <div class="d-flex align-items-center">
          <b-button
            v-if="activeTab === 'blank'"
            @click="toggleAllBlankVorgaengeUndGruppen"
            class="icon-button"
          >
            <Maximize2 v-if="!areAllVorgaengeOpen" :size="20" />
            <Minimize2 v-else :size="20" />
          </b-button>

          <b-button
            v-if="activeTab === 'flugbuchung'"
            @click="toggleAllFlugbuchungVorgaengeUndTeilnehmer"
            class="icon-button"
          >
            <Maximize2 v-if="!areAllVorgaengeOpen" :size="20" />
            <Minimize2 v-else :size="20" />
          </b-button>
        </div>
      </div>

      <div v-if="activeTab === 'blank'">
        <div class="mt-4">
          <b-table
            :items="blankDetailsVorgaenge"
            :fields="blankFields"
            class="flugbuchung-table"
            responsive
            @row-clicked="toggleBlankVorgangRow"
          >
            <template #cell(id)="row">
              <span class="d-flex justify-content-center">
                {{ row.item.id }}
              </span>
            </template>

            <template #cell(pnr)="row">
              <span v-if="row.item.anreiseart === 'KeinFlug'">-</span>
              <span v-else>{{ Array.isArray(row.item.pnr) ? row.item.pnr[0] : row.item.pnr }}</span>
            </template>

            <template #cell(airlinePnr)="row">
              <span v-if="row.item.anreiseart === 'KeinFlug'">-</span>
              <span v-else>
                {{
                  Array.isArray(row.item.airlinePnr)
                    ? row.item.airlinePnr.length > 0
                      ? row.item.airlinePnr[0]
                      : '-'
                    : row.item.airlinePnr || '-'
                }}
              </span>
            </template>

            <template #cell(reisezeitraum)="row">
              <span>
                {{ formatDateWithoutHours(row.item.reisedatum) }} -
                {{ formatDateWithoutHours(row.item.enddatum) }}
              </span>
            </template>

            <template #cell(flugticketAusgestellt)="row">
              <div
                :title="
                  'Anreise Art: ' + row.item.eigenanreiseArt ? row.item.eigenanreiseArt : row.item.anreiseart
                "
                class="text-center"
              >
                <span v-if="row.item.anreiseart === 'KeinFlug' || row.item.anreiseart === 'Eigenanreise'">
                  -
                </span>
                <span v-else class="text-dark-75 font-weight-bolder d-block font-size-lg">
                  <CustomIconChecked :value="row.item.flugticketAusgestellt" />
                </span>
              </div>
            </template>

            <template #head(flugticketAusgestellt)="{ label }">
              <span class="d-inline-flex align-items-center">
                {{ label }}
                <span
                  v-b-tooltip.hover="
                    'Diese Info kommt aus dem B-Tix Satz, welcher nur bei Gruppen gepflegt wird.'
                  "
                >
                  <Asterisk :size="14" class="text-danger mr-1" />
                </span>
              </span>
            </template>

            <!-- Row Content -->
            <template #row-details="row">
              <div class="expandable-row-details p-0">
                <div
                  v-for="group in getBlankTeilnehmerGruppen(row.item)"
                  :key="`${row.item.id}-${group.key}`"
                  :class="[
                    'expandable-row',
                    {
                      'expandable-row-expanded': isBlankGruppeOpen(row.item.id, group.key),
                    },
                  ]"
                >
                  <!-- Teilnehmer Header -->
                  <div
                    class="section-header d-flex align-items-center justify-content-between"
                    @click="toggleBlankGruppe(row.item.id, group.fluege, group.key)"
                    style="cursor: pointer; height: 40px"
                  >
                    <span style="font-weight: 600; font-size: 16px" class="d-flex ml-4">
                      <!-- 1. KeinFlug mit Eigenanreise -->
                      <span
                        v-if="
                          row.item.anreiseart === 'KeinFlug' && group.teilnehmer[0].eigenanreiseArt != null
                        "
                      >
                        <div v-b-tooltip.hover.html title="Kein Flug/<br/>Eigenanreise">
                          <Plane :size="20" color="#F64E60"></Plane>
                        </div>
                      </span>

                      <!-- 2. KeinFlug ohne Eigenanreise -->
                      <span v-else-if="row.item.anreiseart === 'KeinFlug'" class="text-danger">
                        <div v-b-tooltip.hover.top title="Kein Flug">
                          <Plane :size="20" color="#F64E60"></Plane>
                        </div>
                      </span>

                      <!-- 3. Eigenanreise (orange) -->
                      <!-- teilt den string bei vorhandenen kommas und überprüft ob mindestens einer der arten passt -->
                      <span
                        v-else-if="
                          group.teilnehmer[0].eigenanreiseArt
                            ?.split(/,\s*/)
                            .some(art => ['EA', 'EF', 'EAF'].includes(art))
                        "
                      >
                        <div v-b-tooltip.hover.top title="Eigenanreise">
                          <Plane :size="20" color="#FFA800"></Plane>
                        </div>
                      </span>

                      <!-- 4. Normaler Flug (schwarz) -->
                      <span v-else>
                        <Plane :size="20" color="#181D1F"></Plane>
                      </span>

                      <span class="ml-4">{{ group.teilnehmer.map(t => t.name).join(', ') }}</span>
                    </span>
                    <div class="mr-4">
                      <ChevronDown
                        v-if="
                          row.item.anreiseart !== 'KeinFlug' && !isBlankGruppeOpen(row.item.id, group.key)
                        "
                        :size="20"
                      />
                      <ChevronUp
                        v-if="row.item.anreiseart !== 'KeinFlug' && isBlankGruppeOpen(row.item.id, group.key)"
                        :size="20"
                      />
                    </div>
                  </div>

                  <!-- Teilnehmer content -->
                  <b-collapse :visible="isBlankGruppeOpen(row.item.id, group.key)">
                    <div class="flight-table-container">
                      <b-table
                        v-if="group.fluege.length > 0"
                        :items="group.fluege"
                        :fields="[
                          { key: 'pnr', label: 'PNR' },
                          { key: 'airlinePnr', label: 'Airline PNR' },
                          { key: 'Flugstrecke', label: 'Strecke' },
                          { key: 'flugstamm', label: 'Flugstamm' },
                          { key: 'flugnummer', label: 'Flugnr.' },
                          { key: 'datum', label: 'Datum' },
                          { key: 'abflugzeit', label: 'Abflug' },
                          { key: 'ankunftszeit', label: 'Ankunft' },
                        ]"
                        class="blank-flight-table"
                        responsive
                      >
                        <template #head(Flugstrecke)>
                          Flugstrecke
                          <span
                            v-if="hasFluganzahlChanged(row.item, group.teilnehmer[0])"
                            v-b-tooltip.hover.html="{
                              title: getFluganzahlChangeTooltip(row.item, group.teilnehmer[0]),
                              customClass: 'flugaenderung-tooltip',
                            }"
                          >
                            <CircleAlert :size="14" class="text-danger ml-1" />
                          </span>
                        </template>

                        <template #cell(pnr)="flugRow">{{ flugRow.item.pnr ?? '-' }}</template>
                        <template #cell(airlinePnr)="flugRow">{{ flugRow.item.airlinePnr ?? '-' }}</template>

                        <template #cell(Flugstrecke)="flugRow">
                          {{ flugRow.item.abflughafenEigen || flugRow.item.abflughafen }} -
                          {{ flugRow.item.ankunftsflughafenEigen || flugRow.item.ankunftsflughafen }}
                          <span
                            v-if="flugRow.item.oldFlugData"
                            v-b-tooltip.hover.html="{
                              title: getFlugChangeTooltip(flugRow.item.oldFlugData),
                              customClass: 'flugaenderung-tooltip',
                            }"
                          >
                            <CircleAlert :size="14" class="text-warning ml-1" />
                          </span>
                        </template>

                        <template #cell(flugnummer)="flugRow">{{ flugRow.item.flugnummer }}</template>

                        <template #cell(datum)="flugRow">
                          {{ flugRow.item.abflugdatum ?? flugRow.item.ankunftsdatum | date }}
                        </template>

                        <template #cell(abflugzeit)="flugRow">
                          {{
                            flugRow.item.abflugsZeit
                              ? flugRow.item.abflugsZeit + ' Uhr'
                              : $options.filters.time(flugRow.item.abflugdatum)
                          }}
                        </template>

                        <template #cell(ankunftszeit)="flugRow">
                          {{
                            flugRow.item.ankunftsZeit
                              ? flugRow.item.ankunftsZeit + ' Uhr'
                              : $options.filters.time(flugRow.item.ankunftsdatum)
                          }}
                          <span v-if="flugRow.item.ankunftsdatum && flugRow.item.abflugdatum">
                            {{ isArrivalTomorrow(flugRow.item) ? ' +1' : '' }}
                          </span>
                        </template>
                      </b-table>
                      <div v-else style="font-size: 14px; font-weight: 500; padding: 4px">
                        Keine Flüge gefunden
                      </div>
                    </div>
                  </b-collapse>
                </div>
              </div>
            </template>
          </b-table>
        </div>
      </div>

      <div v-if="activeTab === 'flugbuchung'">
        <div class="mt-4">
          <div v-if="isLoadingFlugstrecken" class="text-center p-4">
            <b-spinner />
          </div>

          <b-table
            v-else
            :items="flugbuchungDetailsVorgaenge"
            :fields="flugbuchungFields"
            class="flugbuchung-table"
            responsive
            @row-clicked="toggleFlugbuchungVorgangRow"
          >
            <template #cell(pnr)="row">
              <span v-if="row.item.anreiseart === 'KeinFlug' || row.item.anreiseart === 'Eigenanreise'">
                -
              </span>
              <span v-else>{{ Array.isArray(row.item.pnr) ? row.item.pnr[0] : row.item.pnr }}</span>
            </template>

            <template #cell(statusPruefung)="row">
              <div v-if="row.item.anreiseart === 'KeinFlug'">-</div>
              <div
                v-else
                class="d-flex justify-content-center"
                v-html="statusPruefungConverter[row.item.statusPruefung]"
              ></div>
            </template>

            <template #cell(statusTickets)="row">
              <div v-if="row.item.anreiseart === 'KeinFlug'">-</div>
              <div
                v-else
                class="d-flex justify-content-center"
                v-html="statusTicketsConverter[row.item.statusTickets] || statusTicketsConverter.Critical"
              ></div>
            </template>

            <template #cell(akzeptiert)="row">
              <div class="d-flex justify-content-center" style="gap: 4px">
                <div v-if="row.item.statusPruefungAkzeptiert">
                  <b-badge variant="success">Akzeptiert</b-badge>
                </div>
                <div v-else>
                  <b-badge variant="secondary">Nicht akzeptiert</b-badge>
                </div>
                <div
                  v-if="
                    row.item.warStatusPruefungAkzeptiert &&
                    row.item.statusPruefung !== 'AllGood' &&
                    !row.item.statusPruefungAkzeptiert
                  "
                >
                  <b-badge variant="secondary">War akzeptiert</b-badge>
                </div>
              </div>
            </template>

            <template #cell(releasepruefung)="row">
              <VorgangReleaseprocessProgressBar
                :vorgang="row.item"
                :anreiseart="row.item.anreiseart"
                :passdatenCheckRequired="passdatenCheckRequired"
                :dokumenteCheckRequired="dokumenteCheckRequired"
                :transferData="transferData"
                :isKeineTransferplanungNotwendigHin="
                  reiseterminObj.metadata?.isKeineTransferplanungNotwendigHin
                "
                :isKeineTransferplanungNotwendigRueck="
                  reiseterminObj.metadata?.isKeineTransferplanungNotwendigRueck
                "
                :isReiseOhneFlug="isReiseOhneFlug"
              />
            </template>

            <!-- Row Content -->
            <template #row-details="row">
              <div class="expandable-row-details p-0">
                <div
                  v-for="(teilnehmer, teilnehmerIndex) in row.item.teilnehmers"
                  :key="`${row.item.id}-${teilnehmer.nummer || teilnehmerIndex}`"
                  :class="[
                    'expandable-row',
                    {
                      'expandable-row-expanded': isFlugbuchungTeilnehmerOpen(
                        row.item.id,
                        teilnehmer.nummer || teilnehmerIndex
                      ),
                    },
                  ]"
                >
                  <!-- Teilnehmer Header -->
                  <div
                    class="section-header d-flex align-items-center justify-content-between"
                    @click="
                      toggleFlugbuchungTeilnehmer(
                        row.item.id,
                        row.item.anreiseart,
                        teilnehmer.nummer || teilnehmerIndex
                      )
                    "
                    style="cursor: pointer; height: 40px"
                  >
                    <span style="font-weight: 600; font-size: 16px" class="d-flex ml-4">
                      <!-- 1. KeinFlug mit Eigenanreise -->
                      <span v-if="row.item.anreiseart === 'KeinFlug' && teilnehmer.eigenanreiseArt != null">
                        <div v-b-tooltip.hover.html title="Kein Flug/<br/>Eigenanreise">
                          <Plane :size="20" color="#F64E60"></Plane>
                        </div>
                      </span>

                      <!-- 2. KeinFlug ohne Eigenanreise -->
                      <span v-else-if="row.item.anreiseart === 'KeinFlug'" class="text-danger">
                        <div v-b-tooltip.hover.top title="Kein Flug">
                          <Plane :size="20" color="#F64E60"></Plane>
                        </div>
                      </span>

                      <!-- 3. Eigenanreise (orange) -->
                      <!-- teilt den string bei vorhandenen kommas und überprüft ob mindestens einer der arten passt -->
                      <span
                        v-else-if="
                          teilnehmer.eigenanreiseArt
                            ?.split(/,\s*/)
                            .some(art => ['EA', 'EF', 'EAF'].includes(art))
                        "
                      >
                        <div v-b-tooltip.hover.top title="Eigenanreise">
                          <Plane :size="20" color="#FFA800"></Plane>
                        </div>
                      </span>

                      <!-- 4. Normaler Flug (schwarz) -->
                      <span v-else>
                        <Plane :size="20" color="#181D1F"></Plane>
                      </span>

                      <span class="ml-4">{{ teilnehmer.name }}</span>
                    </span>
                    <div class="mr-4">
                      <ChevronDown
                        v-if="
                          row.item.anreiseart !== 'KeinFlug' &&
                          !isFlugbuchungTeilnehmerOpen(row.item.id, teilnehmer.nummer || teilnehmerIndex)
                        "
                        :size="20"
                      />
                      <ChevronUp
                        v-if="
                          row.item.anreiseart !== 'KeinFlug' &&
                          isFlugbuchungTeilnehmerOpen(row.item.id, teilnehmer.nummer || teilnehmerIndex)
                        "
                        :size="20"
                      />
                    </div>
                  </div>

                  <!-- Teilnehmer content -->
                  <b-collapse
                    :visible="isFlugbuchungTeilnehmerOpen(row.item.id, teilnehmer.nummer || teilnehmerIndex)"
                  >
                    <div class="flight-cards-container d-flex">
                      <!-- Hinflug Card -->
                      <FlugstreckenVergleichHinRueck
                        v-if="getHinflugForTeilnehmer(teilnehmer)"
                        style="flex-grow: 1"
                        :flugstrecke="getHinflugForTeilnehmer(teilnehmer)"
                        :teilnehmer="teilnehmer"
                        :showSollContent="false"
                        istTitle="Hinflug"
                        art="Hinflug"
                        :vorverlaengerungen="getVorverlaengerungenForTeilnehmer(teilnehmer)"
                        :teilnehmerVerlaengerungen="teilnehmer.teilnehmerVerlaengerungen || []"
                      />

                      <!-- Inlandsflug Card -->
                      <FlugstreckenVergleichInlandsfluege
                        v-if="getInlandsflugeForTeilnehmer(teilnehmer).length > 0"
                        style="flex-grow: 1"
                        :flugstrecken="getInlandsflugeForTeilnehmer(teilnehmer)"
                        :showSollContent="false"
                        istTitle="Inlandsflug"
                      />

                      <!-- Rückflug Card -->
                      <FlugstreckenVergleichHinRueck
                        v-if="getRueckflugForTeilnehmer(teilnehmer)"
                        style="flex-grow: 1"
                        :flugstrecke="getRueckflugForTeilnehmer(teilnehmer)"
                        :teilnehmer="teilnehmer"
                        :showSollContent="false"
                        istTitle="Rückflug"
                        art="Rückflug"
                        :verlaengerungen="getVerlaengerungenForTeilnehmer(teilnehmer)"
                        :teilnehmerVerlaengerungen="teilnehmer.teilnehmerVerlaengerungen || []"
                      />

                      <!-- Keine Flugstrecken vorhanden -->
                      <div
                        v-if="
                          !getHinflugForTeilnehmer(teilnehmer) &&
                          !getRueckflugForTeilnehmer(teilnehmer) &&
                          getInlandsflugeForTeilnehmer(teilnehmer).length === 0
                        "
                        style="font-size: 14px; font-weight: 500; padding: 4px"
                      >
                        Es wurden keine Flugbuchungen zu diesem Teilnehmenden gefunden.
                      </div>
                    </div>
                  </b-collapse>
                </div>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import VorgangReleaseprocessProgressBar from '@/components/vorgaenge/vorgang-releaseprocess-progress-bar.vue';
import CustomIconChecked from '@/components/common/custom-icon-checked.vue';
import FlugstreckenVergleichInlandsfluege from '../../vorgaenge/flugstrecken-vergleich-inlandsfluege.vue';
import FlugstreckenVergleichHinRueck from '../../vorgaenge/flugstrecken-vergleich-hinRueck.vue';
import { Maximize2, Minimize2, ChevronUp, ChevronDown } from 'lucide-vue';
import { formatDateWithoutHours, isReiseOhneFlug } from '../../../core/common/helpers/utils';
import * as flightHelpers from '../../../core/common/helpers/reisetermine-card-vorgaenge-fluege-helper';
import { Asterisk, CircleAlert, Plane, Tickets } from 'lucide-vue';
import { lucideHtml } from '@/core/common/helpers/lucide-html';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

export default {
  components: {
    VorgangReleaseprocessProgressBar,
    CustomIconChecked,
    FlugstreckenVergleichInlandsfluege,
    FlugstreckenVergleichHinRueck,
    Maximize2,
    Minimize2,
    ChevronUp,
    ChevronDown,
    Asterisk,
    CircleAlert,
    Plane,
  },

  props: {
    isLoading: { type: Boolean },
    blankVorgaenge: { type: Array },
    passdatenCheckRequired: { type: Boolean },
    dokumenteCheckRequired: { type: Boolean },
    reiseterminObj: { type: Object },
    transferData: { type: Object, default: () => ({}) },
  },

  data() {
    return {
      activeTab: 'blank',
      flugbuchungTeilnehmerOpenState: {},
      blankGruppeOpenState: {},
      flugbuchungOpenState: {},
      blankOpenState: {},
      isLoadingFlugstrecken: false,
      flugstreckenLoaded: false,
      flugbuchungVorgaenge: [],
      statusPruefungConverter: {
        AllGood: lucideHtml(Plane, { size: 20, class: 'text-success' }),
        SomethingWrong: lucideHtml(Plane, { size: 20, class: 'text-warning' }),
        NoSegment: lucideHtml(Plane, { size: 20, class: 'text-danger' }),
      },
      statusTicketsConverter: {
        AllGood: lucideHtml(Tickets, { size: 20, class: 'text-success' }),
        CheckRequired: lucideHtml(Tickets, { size: 20, class: 'text-warning' }),
        Critical: lucideHtml(Tickets, { size: 20, class: 'text-danger' }),
      },
    };
  },

  computed: {
    isReiseOhneFlug() {
      return isReiseOhneFlug(this.reiseterminObj?.metadata);
    },
    isReiseOhneFlugOptionalOhneFluege() {
      return (
        this.reiseterminObj?.metadata?.flugoption === 'ReiseOhneFlugOptionalBuchbar' &&
        this.blankVorgaenge?.every(v => v.anreiseart === 'KeinFlug')
      );
    },
    areAllVorgaengeOpen() {
      const vorgaenge = this.activeTab === 'flugbuchung' ? this.flugbuchungVorgaenge : this.blankVorgaenge;
      if (!vorgaenge || vorgaenge.length === 0) return false;
      const openState = this.activeTab === 'flugbuchung' ? this.flugbuchungOpenState : this.blankOpenState;
      return vorgaenge.every(vorgang => openState[vorgang.id] === true);
    },

    blankVorgaengeById() {
      if (!this.blankVorgaenge) return {};
      return Object.fromEntries(this.blankVorgaenge.map(vorgang => [vorgang.id, vorgang]));
    },

    // Die werte für die icons kommen aus den übergebenen "BlankVorgängen"
    flugbuchungDetailsVorgaenge() {
      if (!this.flugbuchungVorgaenge) return [];
      return this.flugbuchungVorgaenge.map(vorgang => ({
        ...this.blankVorgaengeById[vorgang.id],
        ...vorgang,
        _showDetails: this.flugbuchungOpenState[vorgang.id] || false,
      }));
    },

    blankDetailsVorgaenge() {
      if (!this.blankVorgaenge) return [];
      return this.blankVorgaenge.map(vorgang => ({
        ...vorgang,
        _showDetails: this.blankOpenState[vorgang.id] || false,
      }));
    },

    blankFields() {
      return [
        {
          key: 'id',
          label: 'BNR',
          sortable: true,
          thStyle: { width: '20%' },
          class: 'text-center',
          tdClass: 'align-middle',
        },
        {
          key: 'reisezeitraum',
          label: 'Reisezeitraum',
          sortable: true,
          thStyle: { width: '28%' },
          class: 'text-center',
          tdClass: 'align-middle',
          sortByFormatted: (value, key, item) => {
            const startTime = item.reisedatum ? new Date(item.reisedatum).getTime() : 0;
            const endTime = item.enddatum ? new Date(item.enddatum).getTime() : 0;
            // Multipliziere Startdatum mit großer Zahl, damit es Priorität hat
            return startTime * 1000000 + endTime;
          },
        },
        {
          key: 'flugticketAusgestellt',
          label: 'Flugticket ausgestellt GRP',
          thStyle: { width: '27%' },
          class: 'text-center',
          tdClass: 'align-middle',
        },
        {
          key: 'npx',
          label: 'PAX',
          thStyle: { width: '25%' },
          class: 'text-center',
          tdClass: 'align-middle',
        },
      ];
    },

    flugbuchungFields() {
      return [
        {
          key: 'id',
          label: 'BNR',
          sortable: true,
          thStyle: { width: '11%' },
          class: 'text-center',
          tdClass: 'align-middle',
        },
        {
          key: 'pnr',
          label: 'PNR',
          thStyle: { width: '12%' },
          class: 'text-center',
          tdClass: 'align-middle',
        },
        {
          key: 'npx',
          label: 'PAX',
          thStyle: { width: '15.4%' },
          class: 'text-center',
          tdClass: 'align-middle',
        },
        {
          key: 'statusPruefung',
          label: 'Status Prüfung',
          class: 'text-center',
          thStyle: { width: '15.4%' },
          tdClass: 'align-middle',
        },
        {
          key: 'statusTickets',
          label: 'Status Tickets',
          class: 'text-center',
          thStyle: { width: '15.4%' },
          tdClass: 'align-middle',
        },
        {
          key: 'akzeptiert',
          label: 'Akzeptiert',
          thStyle: { width: '15.4%' },
          class: 'text-center',
          tdClass: 'align-middle',
        },
        {
          key: 'releasepruefung',
          label: 'Releaseprüfung',
          thStyle: { width: '15.4%' },
          class: 'text-center',
          tdClass: 'align-middle',
        },
      ];
    },

    tabs() {
      return [
        {
          show: true,
          value: 'blank',
          text: 'Blank',
        },
        {
          show: true,
          value: 'flugbuchung',
          text: 'Flugbuchung',
        },
      ];
    },
  },

  mounted() {
    if (this.activeTab === 'flugbuchung') {
      this.loadFlugbuchungenAusBOFile();
    }
  },

  watch: {
    activeTab(newTab) {
      if (newTab === 'flugbuchung' && !this.flugstreckenLoaded) {
        this.loadFlugbuchungenAusBOFile();
      }
    },

    blankVorgaenge: {
      handler() {
        this.flugstreckenLoaded = false;
        if (this.activeTab === 'flugbuchung') {
          this.loadFlugbuchungenAusBOFile();
        }
      },
    },
  },

  methods: {
    formatDateWithoutHours,
    isArrivalTomorrow(item) {
      return flightHelpers.isArrivalTomorrow(item);
    },

    //////// Blank TAB Methoden ////////
    // Liefert die Teilnehmer eines Vorgangs zusammengefasst nach gleichem Flug-Set
    getBlankTeilnehmerGruppen(vorgang) {
      return flightHelpers.groupBlankTeilnehmerByFluege(vorgang);
    },

    // Gibt die Keys aller Gruppen zurück
    getBlankGroupKeys(vorgang) {
      return this.getBlankTeilnehmerGruppen(vorgang).map(g => g.key);
    },

    // das "this" gibt an, aus welcher komponente es gerade getriggert wird
    toggleAllBlankVorgaengeUndGruppen() {
      flightHelpers.toggleAllVorgaengeUndTeilnehmer(
        this,
        this.blankOpenState,
        this.blankVorgaenge,
        this.areAllVorgaengeOpen,
        this.blankGruppeOpenState,
        'teilnehmer',
        this.getBlankGroupKeys
      );
    },

    // das "this" gibt an, aus welcher komponente es gerade getriggert wird
    toggleBlankVorgangRow(item) {
      flightHelpers.toggleRow(this, item, this.blankOpenState);
    },

    // das "this" gibt an, aus welcher komponente es gerade getriggert wird
    toggleBlankGruppe(vorgangId, fluegeArray, gruppeKey) {
      if (fluegeArray.length === 0) return;
      const key = flightHelpers.getTeilnehmerKey(vorgangId, gruppeKey);
      this.$set(this.blankGruppeOpenState, key, !this.isBlankGruppeOpen(vorgangId, gruppeKey));
    },

    isBlankGruppeOpen(vorgangId, gruppeKey) {
      const key = flightHelpers.getTeilnehmerKey(vorgangId, gruppeKey);
      const state = this.blankGruppeOpenState[key];
      if (state !== undefined) return state;
      const vorgang = this.blankVorgaengeById[vorgangId];
      return !!(vorgang && this.getBlankTeilnehmerGruppen(vorgang).length === 1);
    },

    getFlugChangeTooltip(oldFlugData) {
      const formatDateTime = dateStr =>
        dateStr ? format(parseISO(dateStr), 'dd.MM.yyyy HH:mm', { locale: de }) + ' Uhr' : '-';

      return `<b>Flugzeitenänderung - Urspr. Strecke:<br/>${oldFlugData.abflughafen}</b>
                ${formatDateTime(oldFlugData.abflugdatum)} -
                <b>${oldFlugData.ankunftsflughafen}</b>
                ${formatDateTime(oldFlugData.ankunftsdatum)}`;
    },

    // Vergleicht die aktuelle Fluganzahl eines Teilnehmers mit dem Stand zum Zeitpunkt der Buchungsfreigabe
    getFluganzahl(vorgang, teilnehmer) {
      const alteFluege = vorgang.allFlights?.fluegeAfterBuchungReleased || [];

      const aktuelleAnzahl = (teilnehmer?.fluege || []).length;
      const alteAnzahl = alteFluege.filter(f => f.nummer === teilnehmer?.nummer).length;

      return { aktuelleAnzahl, alteAnzahl };
    },

    hasFluganzahlChanged(vorgang, teilnehmer) {
      const { aktuelleAnzahl, alteAnzahl } = this.getFluganzahl(vorgang, teilnehmer);
      return alteAnzahl > 0 && aktuelleAnzahl !== alteAnzahl;
    },

    getFluganzahlChangeTooltip(vorgang, teilnehmer) {
      const { aktuelleAnzahl, alteAnzahl } = this.getFluganzahl(vorgang, teilnehmer);
      return `Die Fluganzahl hat sich von ${alteAnzahl} auf ${aktuelleAnzahl} geändert`;
    },

    //////// Flugbuchung TAB Methoden ////////
    getHinflugForTeilnehmer(teilnehmer) {
      return flightHelpers.getHinflugForTeilnehmer(teilnehmer);
    },

    getRueckflugForTeilnehmer(teilnehmer) {
      return flightHelpers.getRueckflugForTeilnehmer(teilnehmer);
    },

    getInlandsflugeForTeilnehmer(teilnehmer) {
      return flightHelpers.getInlandsflugeForTeilnehmer(teilnehmer);
    },

    getVerlaengerungenForTeilnehmer(teilnehmer) {
      return flightHelpers.getVerlaengerungenForTeilnehmer(teilnehmer);
    },

    getVorverlaengerungenForTeilnehmer(teilnehmer) {
      return flightHelpers.getVorverlaengerungenForTeilnehmer(teilnehmer);
    },

    // das "this" gibt an, aus welcher komponente es gerade getriggert wird
    toggleAllFlugbuchungVorgaengeUndTeilnehmer() {
      flightHelpers.toggleAllVorgaengeUndTeilnehmer(
        this,
        this.flugbuchungOpenState,
        this.flugbuchungVorgaenge,
        this.areAllVorgaengeOpen,
        this.flugbuchungTeilnehmerOpenState,
        'teilnehmers'
      );
    },

    // das "this" gibt an, aus welcher komponente es gerade getriggert wird
    toggleFlugbuchungVorgangRow(item) {
      flightHelpers.toggleRow(this, item, this.flugbuchungOpenState);
    },

    // das "this" gibt an, aus welcher komponente es gerade getriggert wird
    toggleFlugbuchungTeilnehmer(vorgangId, anreiseart, teilnehmerId) {
      if (anreiseart === 'KeinFlug') return;
      const key = flightHelpers.getTeilnehmerKey(vorgangId, teilnehmerId);
      this.$set(
        this.flugbuchungTeilnehmerOpenState,
        key,
        !this.isFlugbuchungTeilnehmerOpen(vorgangId, teilnehmerId)
      );
    },

    isFlugbuchungTeilnehmerOpen(vorgangId, teilnehmerId) {
      const key = flightHelpers.getTeilnehmerKey(vorgangId, teilnehmerId);
      const state = this.flugbuchungTeilnehmerOpenState[key];
      if (state !== undefined) return state;
      const vorgang = this.flugbuchungVorgaenge?.find(v => v.id === vorgangId);
      return vorgang?.teilnehmers?.length === 1;
    },

    async loadFlugbuchungenAusBOFile() {
      if (!this.blankVorgaenge || this.blankVorgaenge.length === 0) return;
      if (this.flugstreckenLoaded) return;

      this.isLoadingFlugstrecken = true;
      try {
        this.flugbuchungVorgaenge = await flightHelpers.loadFlugbuchungenAusBOFile(this.blankVorgaenge);
        this.flugstreckenLoaded = true;
      } catch (error) {
        console.error('Error loading detailed flight data:', error);
      } finally {
        this.isLoadingFlugstrecken = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/sass/components/expandable-section-styles.scss';

.expandable-row .section-header {
  box-shadow: 0px 2px 4px 0px #0000001a;
}

// Tabelle sitzt bündig unter dem Header
.expandable-row .flight-table-container {
  margin-top: 0;
  border-radius: 0;
}

// Im ausgeklappten Zustand -> Header und Tabelle visuell zu einer Card zusammen
.expandable-row.expandable-row-expanded .section-header {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: none;
}

// Im Flugbuchung-Tab werden Hinflug, Inlandsflug und Rückflug nebeneinander angezeigt
.flight-cards-container {
  flex-wrap: nowrap;

  > * {
    min-width: 0;
    flex-basis: 0;
  }

  :deep(.compare-container) {
    width: auto;
    min-width: 0;
  }
}
</style>

<style lang="scss">
.flugaenderung-tooltip {
  .tooltip-inner {
    max-width: 350px;
    white-space: nowrap;
  }
}
</style>
